import { useHandleUnauthorized } from "@/components/hooks/use-handle-unauthorized";
import { useRouteHandler } from "@/components/hooks/use-route-handler";
import { logger } from "@/utils/logger";
import { useMemo, useRef } from "react";
import { updateLastCheckedForEmployer } from "./in-app-notifications";

const refreshIntervalMs = 5 * 60 * 1000; // 5分間隔でポーリング

export function useInAppNotifications({ officeId }: { officeId: string }) {
  const handleUnauthorized = useHandleUnauthorized();

  // 最新通知・最終確認時刻・未読状態の管理
  const { data, isLoading, mutate } = useRouteHandler(
    "/employers/[officeId]/api/in-app-notifications",
    { params: { officeId } },
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      refreshInterval: refreshIntervalMs,
      // セッション切れ・事業所から外れた場合はログイン画面に誘導する
      onError: (error) => {
        if (error.status === 401) {
          handleUnauthorized();
        }
      },
    },
  );

  const notifications = useMemo(() => data?.notifications ?? [], [data]);

  const lastCheckedAt = data?.lastCheckedAt
    ? new Date(data.lastCheckedAt)
    : null;

  const hasUnread = data?.hasUnread ?? false;

  // 最終確認時刻の更新処理。閉じた時の再取得は、この完了を待ってから行う
  const pendingUpdateRef = useRef<Promise<unknown>>(Promise.resolve());

  const markAsReadAndUpdateLastChecked = () => {
    const markAsRead = (current: typeof data) =>
      current && { ...current, hasUnread: false };

    // 最終確認時刻を更新し、キャッシュを既読にする（最終確認時刻のキャッシュは更新しない）
    // 更新の完了前に始まった取得の結果は SWR が破棄するため、ポーリングなどで既読が未読に戻らない
    pendingUpdateRef.current = mutate(
      async (current) => {
        await updateLastCheckedForEmployer({ officeId });
        return markAsRead(current);
      },
      {
        // 未取得の場合は既読にするキャッシュがないので、楽観更新しない
        optimisticData: markAsRead(data),
        revalidate: false,
        // 更新に失敗しても既読のままにする
        rollbackOnError: false,
      },
    ).catch((error) => {
      logger.warn({ error, officeId }, "Failed to update last checked");
    });
  };

  const refreshLastChecked = async () => {
    // 更新前の未読状態で上書きしないよう、最終確認時刻の更新が終わってから再取得する
    await pendingUpdateRef.current;
    mutate();
  };

  return {
    hasUnread,
    notifications,
    isLoading,
    lastCheckedAt,
    markAsReadAndUpdateLastChecked,
    refreshLastChecked,
  };
}
